import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import en from '../i18n/en';
import ar from '../i18n/ar';

const DICTS = { en, ar };
const STORAGE_KEY = 'fmflexi_lang';

// Build a reverse lookup (arabic-string → english-string) once, at module load,
// so the global tooltip observer can translate any Arabic text it finds in the
// DOM back to English without needing per-component retrofits.
const AR_TO_EN = Object.create(null);
Object.keys(ar).forEach((key) => {
  const arVal = ar[key];
  const enVal = en[key];
  if (typeof arVal === 'string' && typeof enVal === 'string' && arVal !== enVal) {
    AR_TO_EN[arVal.trim()] = enVal;
  }
});

export const LanguageContext = createContext({
  lang: 'en',
  dir: 'ltr',
  t: (k) => k,
  setLang: () => {},
  arabicAvailable: false,
});

/**
 * LanguageProvider
 * - lang persisted in localStorage
 * - if `arabicAvailable` (per-company toggle) is false, lang is forced to 'en'
 * - applies dir + lang to <html> on the fly
 */
export function LanguageProvider({ children, arabicAvailable = false, ready = true }) {
  const [lang, setLangState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || 'en';
    } catch {
      return 'en';
    }
  });

  // If arabic was disabled by company, force English — but ONLY after the
  // company query has resolved. During the initial load `company` may still
  // be null (arabicAvailable=false) even for Arabic-enabled tenants; resetting
  // here prematurely would overwrite the user's localStorage preference to 'en'.
  useEffect(() => {
    if (!ready) return;
    if (!arabicAvailable && lang === 'ar') {
      setLangState('en');
      try { localStorage.setItem(STORAGE_KEY, 'en'); } catch {}
    }
  }, [arabicAvailable, lang, ready]);

  // Apply dir + lang to <html>
  useEffect(() => {
    const dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', lang);
  }, [lang]);

  // Global Arabic → English hover tooltip (Phase 18.6).
  // When the UI is rendered in Arabic, observe the DOM and set a `title`
  // attribute with the English translation on any element whose text content
  // matches a known Arabic string. This gives every Arabic word a hover
  // tooltip without retrofitting every component — mouse-hover surfaces the EN.
  useEffect(() => {
    if (lang !== 'ar') {
      // cleanup: drop any tooltips we set last time
      document.querySelectorAll('[data-tt-auto="1"]').forEach((el) => {
        el.removeAttribute('data-tt-en');
        el.removeAttribute('data-tt-auto');
        el.classList.remove('tt-ar-hint');
      });
      return;
    }
    const tagWithEnTitle = (el) => {
      if (!el || el.nodeType !== 1) return;
      // skip inputs/selects/textareas (title on form controls is weird)
      const tag = el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || tag === 'OPTION' || tag === 'SCRIPT' || tag === 'STYLE') return;
      // only leaf/text-only nodes (element containing just text) to avoid
      // polluting big composite regions
      if (el.children && el.children.length > 0) return;
      const txt = (el.textContent || '').trim();
      if (!txt || txt.length > 200) return;
      const enVal = AR_TO_EN[txt];
      if (!enVal) return;
      if (el.getAttribute('data-tt-en') === enVal) return;
      el.setAttribute('data-tt-en', enVal);
      el.setAttribute('data-tt-auto', '1');
      el.classList.add('tt-ar-hint');
    };
    const sweep = (root = document.body) => {
      if (!root || !root.querySelectorAll) return;
      // walk every element — cheap enough for typical page sizes
      root.querySelectorAll('*').forEach(tagWithEnTitle);
    };
    sweep();
    const obs = new MutationObserver((records) => {
      for (const r of records) {
        r.addedNodes.forEach((n) => {
          if (n.nodeType === 1) sweep(n);
        });
        if (r.type === 'characterData' && r.target?.parentElement) {
          tagWithEnTitle(r.target.parentElement);
        }
      }
    });
    obs.observe(document.body, { childList: true, subtree: true, characterData: true });
    return () => obs.disconnect();
  }, [lang]);

  const setLang = useCallback((newLang) => {
    if (newLang !== 'en' && newLang !== 'ar') return;
    if (newLang === 'ar' && !arabicAvailable) return;
    setLangState(newLang);
    try { localStorage.setItem(STORAGE_KEY, newLang); } catch {}
  }, [arabicAvailable]);

  const t = useCallback((key, vars) => {
    const dict = DICTS[lang] || DICTS.en;
    let val = dict[key];
    if (val == null) val = DICTS.en[key]; // fallback to English
    if (val == null) return key; // last resort: return the key
    if (vars && typeof val === 'string') {
      Object.keys(vars).forEach(v => {
        val = val.replace(new RegExp(`{{\\s*${v}\\s*}}`, 'g'), String(vars[v]));
      });
    }
    return val;
  }, [lang]);

  // Always-English version of t() — used to power the hover tooltip when
  // the UI is rendered in Arabic (Phase 18.6).
  const tEn = useCallback((key, vars) => {
    let val = DICTS.en[key];
    if (val == null) return key;
    if (vars && typeof val === 'string') {
      Object.keys(vars).forEach(v => {
        val = val.replace(new RegExp(`{{\\s*${v}\\s*}}`, 'g'), String(vars[v]));
      });
    }
    return val;
  }, []);

  const value = useMemo(() => ({
    lang,
    dir: lang === 'ar' ? 'rtl' : 'ltr',
    t,
    tEn,
    setLang,
    arabicAvailable
  }), [lang, t, tEn, setLang, arabicAvailable]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useT() {
  const ctx = useContext(LanguageContext);
  return ctx.t;
}

export function useLanguage() {
  return useContext(LanguageContext);
}

/**
 * <Tt k="nav.dashboard" />
 * Renders the current-language string. When UI is in Arabic, adds a `title`
 * attribute with the English translation so hovering shows the EN version.
 * Also renders a subtle underline in AR so users know they can hover.
 */
export function Tt({ k, vars, className = '', as: Tag = 'span' }) {
  const ctx = useContext(LanguageContext);
  const current = ctx.t(k, vars);
  if (ctx.lang !== 'ar') {
    return <Tag className={className}>{current}</Tag>;
  }
  const english = ctx.tEn(k, vars);
  const hoverClass = 'tt-ar-hint';
  return (
    <Tag
      aria-label={english}
      data-tt-en={english}
      data-tt-auto="1"
      className={`${hoverClass} ${className}`.trim()}
    >
      {current}
    </Tag>
  );
}
